import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { useAuth } from './AuthContext';
import { API } from '../lib/api';

const WishlistContext = createContext();
const STORAGE_KEY = 'sdny_wishlist_v1';

// Helper functions for guest wishlist (localStorage)
function getGuestWishlist() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveGuestWishlist(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.warn('Failed to save wishlist to localStorage', e);
  }
}

function clearGuestWishlist() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

function reducer(state, action) {
  switch (action.type) {
    case 'INIT':
      return action.items;
    case 'ADD':
      if (state.some(i => i.key === action.item.key)) return state;
      return [...state, action.item];
    case 'REMOVE':
      return state.filter(i => i.key !== action.key);
    case 'CLEAR':
      return [];
    default:
      return state;
  }
}

export function WishlistProvider({ children }) {
  const { user, token, loading: authLoading } = useAuth();
  const isAuthenticated = !!user && !!token;
  const [items, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);

  // Load wishlist on mount or auth change
  useEffect(() => {
    if (authLoading) return;
    
    const loadWishlist = async () => {
      setLoading(true);
      
      if (isAuthenticated) {
        // User is logged in: sync guest wishlist then load from server
        try {
          const guestWishlist = getGuestWishlist();
          
          // Sync guest wishlist to server if it has items
          if (guestWishlist.length > 0) {
            for (const item of guestWishlist) {
              try {
                await API.post('/wishlist/add', {
                  product_id: item.product_id,
                  variant_id: item.variant_id || '',
                  product_name: item.product_name || item.title || '',
                  image: item.image || item.image_url || '',
                  description: item.description || null,
                  price: item.price || 0,
                  item_type: item.item_type || 1
                });
              } catch (err) {
                console.error('Error syncing wishlist item:', err);
              }
            }
            clearGuestWishlist();
          }
          
          // Fetch user's wishlist from server
          const response = await API.get('/wishlist');
          const serverItems = (response.data || []).map(item => ({
            ...item,
            key: `${item.product_id}_${item.variant_id || ''}`
          }));
          
          dispatch({ type: 'INIT', items: serverItems });
        } catch (error) {
          console.error('Error loading user wishlist:', error);
        } finally {
          setLoading(false);
        }
      } else {
        // Guest user: load from localStorage
        try {
          const guestWishlist = getGuestWishlist();
          const itemsWithKeys = guestWishlist.map(item => ({
            ...item,
            key: item.key || `${item.product_id}_${item.variant_id || ''}`
          }));
          dispatch({ type: 'INIT', items: itemsWithKeys });
        } catch (error) {
          console.error('Error loading guest wishlist:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadWishlist();
  }, [isAuthenticated, authLoading]);

  // Persist guest wishlist to localStorage when it changes
  useEffect(() => {
    if (!authLoading && !isAuthenticated && items.length >= 0) {
      saveGuestWishlist(items);
    }
  }, [items, isAuthenticated, authLoading]);

  const add = async (data) => {
    const key = `${data.product_id}_${data.variant_id || ''}`;
    const item = { ...data, key };
    
    // Check if already in wishlist
    if (items.some(i => i.key === key)) {
      return;
    }
    
    if (isAuthenticated) {
      // Send to backend
      try {
        await API.post('/wishlist/add', {
          product_id: data.product_id,
          variant_id: data.variant_id || '',
          product_name: data.product_name || data.title || '',
          image: data.image || data.image_url || '',
          description: data.description || null,
          price: data.price || 0,
          item_type: data.item_type || 1
        });
        
        // Re-fetch to ensure sync
        const response = await API.get('/wishlist');
        const serverItems = (response.data || []).map(i => ({
          ...i,
          key: `${i.product_id}_${i.variant_id || ''}`
        }));
        dispatch({ type: 'INIT', items: serverItems });
      } catch (error) {
        console.error('Error adding to wishlist:', error);
        throw error;
      }
    } else {
      // Guest user - store in localStorage
      dispatch({ type: 'ADD', item });
    }
  };

  const remove = async (key) => {
    if (isAuthenticated) {
      // Parse key to get product_id and variant_id
      const parts = key.split('_');
      const product_id = parts[0];
      const variant_id = parts.slice(1).join('_');
      
      try {
        await API.delete('/wishlist/remove', {
          params: { product_id, variant_id }
        });
        
        // Re-fetch to ensure sync
        const response = await API.get('/wishlist');
        const serverItems = (response.data || []).map(i => ({
          ...i,
          key: `${i.product_id}_${i.variant_id || ''}`
        }));
        dispatch({ type: 'INIT', items: serverItems });
      } catch (error) {
        console.error('Error removing from wishlist:', error);
      }
    } else {
      // Guest user
      dispatch({ type: 'REMOVE', key });
    }
  };

  const clear = () => {
    dispatch({ type: 'CLEAR' });
    if (!isAuthenticated) {
      clearGuestWishlist();
    }
  };

  return (
    <WishlistContext.Provider value={{ items, add, remove, clear, loading }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  return useContext(WishlistContext);
}
